import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { getRegistryEntry, hasRecordPermission, type RecordType } from './registry';
import { useRecordViewer } from './RecordViewer';

export interface RecordLinkProps {
  type: RecordType;
  /** Missing id = plain text. */
  id?: string | null;
  /** Full number, never truncated. */
  label: string;
  context?: Record<string, string>;
  variant?: 'inline' | 'mono';
  className?: string;
  testId?: string;
}

/**
 * A reference to another record that opens in place in the record viewer (never navigates away).
 * Plain text, with no tooltip and nothing disabled, when the id is missing, the type is not
 * registered or the user lacks the type's permission (the backend enforces it). Prints as plain text.
 */
export function RecordLink({ type, id, label, context, variant = 'inline', className, testId }: RecordLinkProps) {
  const { t } = useTranslation('records');
  const has = useAuthStore((s) => s.hasPermission);
  const { open } = useRecordViewer();
  const entry = getRegistryEntry(type);

  if (!id || !entry || !hasRecordPermission(entry.permission, has)) {
    return (
      <span className={cn(variant === 'mono' && 'font-mono', className)}>
        <bdi dir="ltr">{label}</bdi>
      </span>
    );
  }

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    open({ type, id, label, context }, { returnFocusTo: e.currentTarget });
  };

  return (
    <button
      type="button"
      data-testid={testId ?? 'record-link'}
      data-record-link=""
      data-record-type={type}
      aria-haspopup="dialog"
      aria-label={t('openRecord', { type: t(entry.labelKey), number: label })}
      onClick={onClick}
      className={cn(
        'inline text-start text-sm font-medium text-primary-text underline-offset-2 hover:underline',
        'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'print:text-foreground print:no-underline',
        variant === 'mono' && 'font-mono',
        className,
      )}
    >
      <bdi dir="ltr">{label}</bdi>
    </button>
  );
}
